import type { AttendanceBalanceBreakdownsQueryVariables } from 'generated/resources/graphql'
import {
  AttendanceBalanceBreakdownsDocument,
  AttendanceBalanceBreakdownsQuery,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useQuery } from 'modules/core/hooks/useQuery'
import {
  dateBackendToUI,
  formatBackendDateToMonth,
  formatBackendDateToYear,
} from 'modules/core/lib/dates'
import { minutesToText } from 'modules/core/lib/duration'

import type Period from 'modules/attendance/store/models/Period'

export type BalanceBreakdownType =
  AttendanceBalanceBreakdownsQuery['attendance']['balanceBreakdownsConnection']['nodes'][number]

type AttendanceBalanceOvertimePool = BalanceBreakdownType['overtimePools']['balance']

export const useFetchAttendanceBalanceBreakdowns = (
  variables: AttendanceBalanceBreakdownsQueryVariables,
  skipFetch: { skip: boolean } = { skip: false }
) => {
  const { loading, data, refetch } = useQuery(AttendanceBalanceBreakdownsDocument, {
    variables,
    skip: skipFetch?.skip,
  })
  const balanceBreakdowns = data?.attendance.balanceBreakdownsConnection.nodes.map((breakdown) => {
    return new BalanceBreakdown(breakdown)
  })

  return {
    loading,
    balanceBreakdowns,
    refetch,
  }
}

export class BalanceBreakdown {
  balanceBreakdown: BalanceBreakdownType

  constructor(balanceBreakdown: BalanceBreakdownType) {
    this.balanceBreakdown = balanceBreakdown
  }

  get employeeId() {
    return this.balanceBreakdown.employeeId
  }

  get duration() {
    const firstPeriod = this.endAt
    const lastPeriod = this.startAt

    const endAt = dateBackendToUI(firstPeriod)
    const startAt = dateBackendToUI(lastPeriod)

    return { startAt, endAt }
  }

  get startAt(): string {
    return this.balanceBreakdown.startAt
  }

  get endAt(): string {
    return this.balanceBreakdown.endAt
  }

  get month(): string {
    return formatBackendDateToMonth(this.balanceBreakdown.startAt)
  }

  get year(): string {
    return formatBackendDateToYear(this.balanceBreakdown.startAt)
  }

  get isCurrentPeriod(): boolean {
    return moment().startOf('month').isSame(moment(this.endAt).startOf('month'))
  }

  get pools() {
    return this.balanceBreakdown.overtimePools
  }

  isValidFor(customPeriod: Period): boolean {
    return (
      this.balanceBreakdown.employeeId === customPeriod.attributes.employee_id &&
      this.balanceBreakdown.endAt === customPeriod.attributes.end_on
    )
  }

  // Accumulation
  get accumulationEnabled(): boolean {
    return this.balanceBreakdown.accumulationEnabled
  }

  // Pool helper methods
  isPoolEnabled(pool: AttendanceBalanceOvertimePool): boolean {
    return pool.enabled
  }

  isPoolUnlimited(pool: AttendanceBalanceOvertimePool): boolean {
    return pool.unlimited
  }

  isPoolMonthlyUpdated(pool: AttendanceBalanceOvertimePool): boolean {
    return this.isPoolEnabled(pool) && pool.updateFrequency == 'monthly'
  }

  isPoolLimitedCapacity(pool: AttendanceBalanceOvertimePool): boolean {
    return this.poolCapacity(pool) != Infinity && this.poolCapacity(pool) >= 0
  }

  poolCapacity(pool: AttendanceBalanceOvertimePool): number {
    if (this.isPoolUnlimited(pool)) return Infinity

    return +(pool.capacity || '')
  }

  poolBalance(pool: AttendanceBalanceOvertimePool): number {
    return +pool.minutes
  }

  poolTransfers(pool: AttendanceBalanceOvertimePool): number {
    return +pool.transfers
  }

  poolPreviousBalance(pool: AttendanceBalanceOvertimePool): number {
    return +pool.previousMinutes
  }

  poolPreviousTransfers(pool: AttendanceBalanceOvertimePool): number {
    return +pool.previousTransfers
  }

  // Balance
  get rawBalance(): number {
    return +this.balanceBreakdown.rawBalanceMinutes
  }

  get balance(): number {
    return +this.balanceBreakdown.balanceMinutes
  }

  // TimeOff + Payroll
  get compensationBreakdown(): boolean {
    return this.timeoffBreakdown || this.payrollBreakdown
  }

  get timeoffBreakdown(): boolean {
    return (
      !this.isPoolUnlimited(this.balancePool) &&
      this.isPoolEnabled(this.timeoffPool) &&
      this.poolCapacity(this.timeoffPool) > 0
    )
  }

  get payrollBreakdown(): boolean {
    return this.isPoolEnabled(this.payrollPool)
  }

  get availablePayrollCompensation(): string {
    if (this.isCurrentPeriod) return '-'

    const minutes = Math.max(
      this.poolBalance(this.balancePool) + this.poolPreviousBalance(this.balancePool),
      0
    )
    return minutesToText(minutes)
  }

  get discardedPool() {
    return this.balanceBreakdown.overtimePools.discarded
  }
  get timeoffPool() {
    return this.balanceBreakdown.overtimePools.timeoff
  }
  get balancePool() {
    return this.balanceBreakdown.overtimePools.balance
  }
  get payrollPool() {
    return this.balanceBreakdown.overtimePools.payroll
  }

  // Discarded
  get discardedBreakdown(): boolean {
    return (
      this.isPoolEnabled(this.discardedPool) &&
      this.poolTransfers(this.discardedPool) !== 0 &&
      !(this.isCurrentPeriod && this.isPoolMonthlyUpdated(this.timeoffPool)) &&
      ((this.isPoolLimitedCapacity(this.balancePool) && !this.isPoolEnabled(this.timeoffPool)) ||
        (this.isPoolLimitedCapacity(this.timeoffPool) && this.isPoolEnabled(this.timeoffPool)))
    )
  }
}
